import { type FC, useEffect } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentIntroData, useSubmitEnrollmentIntroData } from '@egw/api'
import { Button, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { EnrollmentLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader, ContentBlock } from '../../../components'
import { ROUTES } from '../../../constants'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Intro')

export const IntroPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentIntroData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentIntroData()

  useValidateRoute(data?.path)

  const handleLinkClick = async (): Promise<void> => {
    try {
      const response = await submit({
        proceedIntro: true,
      })
      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  }

  return (
    <EnrollmentLayout title="Intro">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.intro.title')} />
            <ContentBlock title={t('investments.intro.tax_title')} content={t('investments.intro.tax_description')} />
            <ContentBlock
              title={t('investments.intro.advisors_title')}
              content={t('investments.intro.advisors_description')}
            />
            <ContentBlock
              title={t('investments.intro.start_title')}
              content={t('investments.intro.start_description')}
            />
            <div className="flex-1 md:hidden" />
            <Button
              type="button"
              className="w-full md:w-min mt-lg"
              testId="enrollment-close-button"
              onClick={() => {
                void handleLinkClick()
              }}
            >
              {t('common.continue')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {!isLoading && !isSubmitting && (error ?? submitError) ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
